import React, { useState, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import PanelGroup from './PanelGroup';
import { FiPlus } from 'react-icons/fi';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDraggable,
  useDroppable,
  rectIntersection
} from '@dnd-kit/core';
import { useEditing } from './EditingContext';

// Draggable MainGroup-Komponente
function DraggableMainGroup({ group, isAuthor, children }) {
  const { showEditButtons, isEditing } = useEditing();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: group.id,
    disabled: !isAuthor || !showEditButtons || isEditing
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  );
}

// Separate Droppable-Zone für das Befördern von Untergruppen zu Hauptgruppen
function DroppablePromoteZone({ onAddGroup }) {
  const { showEditButtons, isEditing } = useEditing();
  const { isOver, setNodeRef } = useDroppable({
    id: 'promote-to-main',
    disabled: !showEditButtons || isEditing
  });

  return (
    <div 
      ref={setNodeRef}
      style={{
        marginTop: '24px',
        padding: '24px',
        border: isOver ? '3px solid #4caf50' : '2px dashed #bbb',
        borderRadius: '12px',
        backgroundColor: isOver ? '#e8f5e8' : '#f8f9f8',
        color: isOver ? '#2e7d32' : '#666',
        fontWeight: isOver ? 'bold' : 'normal',
        fontSize: '16px',
        transition: 'all 0.3s ease',
        cursor: isOver ? 'copy' : 'default',
        minHeight: '80px',
        position: 'relative'
      }}
    >

      <div style={{
        position: 'sticky',
        left: '5px',
        zIndex: 5,
        maxWidth: '90%',
        marginBottom: '12px',
        backgroundColor: isOver ? '#e8f5e8' : '#f8f9f8',
        padding: '4px 8px',
        borderRadius: '4px',
        width: 'fit-content'
      }}>
        {isOver 
          ? 'Hier ablegen um zur Hauptgruppe zu befördern!' 
          : 'Ziehen Sie Untergruppen hierher, um sie zu Hauptgruppen zu machen oder erstellen Sie eine neue'
        }
      </div>
      <button
        onClick={onAddGroup}
        style={{
          background: '#222',
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          padding: '12px 16px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '500',
          transition: 'all 0.2s ease',
          position: 'sticky',
          left: '5px',
          zIndex: 5
        }}
          onMouseOver={(e) => {
            e.target.style.background = '#333';
          }}
          onMouseOut={(e) => {
            e.target.style.background = '#222';
          }}
          title="Neue Hauptgruppe hinzufügen"
        >
          <FiPlus size={18} />
          Neue Hauptgruppe erstellen
        </button>
    </div>
  );
}

const PanelGroupsContainer = forwardRef(({ isAuthor, onDataChange, visibleWeeks = 10, weekWidth = 200, isVisibleWeeksChanging = false, isZoomChanging = false, onInfoPanelsChange, batchOperations, onCheckForDuplicates }, ref) => {
  return (
    <PanelGroupsContainerInner isAuthor={isAuthor} onDataChange={onDataChange} visibleWeeks={visibleWeeks} weekWidth={weekWidth} isVisibleWeeksChanging={isVisibleWeeksChanging} isZoomChanging={isZoomChanging} onInfoPanelsChange={onInfoPanelsChange} batchOperations={batchOperations} onCheckForDuplicates={onCheckForDuplicates} ref={ref} />
  );
});

export default PanelGroupsContainer;

const PanelGroupsContainerInner = forwardRef(({ isAuthor, onDataChange, visibleWeeks = 10, weekWidth = 200, isVisibleWeeksChanging = false, isZoomChanging = false, onInfoPanelsChange, batchOperations, onCheckForDuplicates }, ref) => {
  return (
    <PanelGroupsContainerInternal isAuthor={isAuthor} onDataChange={onDataChange} visibleWeeks={visibleWeeks} weekWidth={weekWidth} isVisibleWeeksChanging={isVisibleWeeksChanging} isZoomChanging={isZoomChanging} onInfoPanelsChange={onInfoPanelsChange} batchOperations={batchOperations} onCheckForDuplicates={onCheckForDuplicates} ref={ref} />
  );
});

const PanelGroupsContainerInternal = forwardRef(({ isAuthor, onDataChange, visibleWeeks = 10, weekWidth = 200, isVisibleWeeksChanging = false, isZoomChanging = false, onInfoPanelsChange, batchOperations, onCheckForDuplicates }, ref) => {
  
  const [groups, setGroupsInternal] = useState([
    
  ]);
  const [showDialog, setShowDialog] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [activePanel, setActivePanel] = useState(null);
  const [isPromoting, setIsPromoting] = useState(false);

  // Wrapper-Funktion für setGroups, verwende einfach setGroupsInternal
  const setGroups = useCallback((newGroups) => {
    setGroupsInternal(newGroups);
  }, []);

  // Hilfsfunktion zum Zusammenführen von Panels innerhalb einer Gruppe
  const mergeDuplicatePanelsInGroup = useCallback((panels) => {
    if (!panels || panels.length === 0) {
      return { panels, mergedCount: 0 };
    }

    // Sammle alle normalen Panels (keine Untergruppen)
    const normalPanels = panels.filter(panel => panel.type !== 'group');
    const groupPanels = panels.filter(panel => panel.type === 'group');
    
    // Finde Duplikate unter den normalen Panels
    const nameCountMap = new Map();
    normalPanels.forEach(panel => {
      const trimmedTitle = panel.title?.trim();
      if (trimmedTitle) {
        nameCountMap.set(trimmedTitle, (nameCountMap.get(trimmedTitle) || 0) + 1);
      }
    });

    const duplicateNames = Array.from(nameCountMap.entries())
      .filter(([, count]) => count > 1)
      .map(([name]) => name);

    if (duplicateNames.length === 0) {
      // Keine Duplikate in normalen Panels, verarbeite nur Untergruppen rekursiv
      const processedGroupPanels = groupPanels.map(panel => {
        const result = mergeDuplicatePanelsInGroup(panel.panels);
        return {
          ...panel,
          panels: result.panels
        };
      });
      
      // Sammle alle merge counts aus Untergruppen
      const totalRecursiveMerges = groupPanels.reduce((total, panel) => {
        const result = mergeDuplicatePanelsInGroup(panel.panels);
        return total + result.mergedCount;
      }, 0);
      
      return { 
        panels: [...normalPanels, ...processedGroupPanels], 
        mergedCount: totalRecursiveMerges 
      };
    }

    // Verarbeite Duplikate
    const newPanels = [];
    const processedNames = new Set();
    let mergedCount = 0;

    // Verarbeite normale Panels mit Duplikaten
    normalPanels.forEach(panel => {
      const trimmedTitle = panel.title?.trim();
      
      if (!trimmedTitle || !duplicateNames.includes(trimmedTitle)) {
        // Kein Duplikat, direkt hinzufügen
        newPanels.push(panel);
      } else if (!processedNames.has(trimmedTitle)) {
        // Erstes Panel mit diesem Namen - sammle alle Duplikate
        processedNames.add(trimmedTitle);
        
        const panelsWithSameName = normalPanels.filter(p => 
          p.title?.trim() === trimmedTitle
        );
        
        if (panelsWithSameName.length > 1) {
          // Füge das erste Panel als Basis hinzu und sammle alle movableBars
          const mergedPanel = { ...panelsWithSameName[0] };
          const allMovableBars = [];
          
          let nextBarId = 1; // Starte mit ID 1 für neue eindeutige IDs
          panelsWithSameName.forEach((p) => {
            if (p.movableBars && Array.isArray(p.movableBars)) {
              // Gib jedem MovableBar eine neue eindeutige ID
              const barsWithNewIds = p.movableBars.map(bar => ({
                ...bar,
                id: nextBarId++
              }));
              allMovableBars.push(...barsWithNewIds);
            }
          });
          
          // Ordne MovableBars neu an, um Überlappungen zu vermeiden
          const repositionedBars = allMovableBars.map((bar, index) => ({
            ...bar,
            y: index * 50 // Jeder Task 50px auseinander (oder eine andere geeignete Höhe)
          }));
          
          mergedPanel.movableBars = repositionedBars;
          newPanels.push(mergedPanel);
          mergedCount += panelsWithSameName.length - 1;
        } else {
          newPanels.push(panelsWithSameName[0]);
        }
      }
    });

    // Verarbeite Untergruppen rekursiv
    const processedGroupPanels = groupPanels.map(panel => {
      const result = mergeDuplicatePanelsInGroup(panel.panels);
      mergedCount += result.mergedCount;
      return {
        ...panel,
        panels: result.panels
      };
    });

    const result = { 
      panels: [...newPanels, ...processedGroupPanels], 
      mergedCount 
    };
    
    return result;
  }, []);

  // Funktion zum Prüfen, ob Duplikate vorhanden sind (ohne zusammenzuführen)
  const checkForDuplicates = useCallback((includePanels = false) => {
    if (!groups || groups.length === 0) return { hasGroupDuplicates: false, hasPanelDuplicates: false };

    // Prüfe Gruppenduplikate
    const groupNameCountMap = new Map();
    groups.forEach(group => {
      const trimmedTitle = group.title?.trim();
      if (trimmedTitle) {
        groupNameCountMap.set(trimmedTitle, (groupNameCountMap.get(trimmedTitle) || 0) + 1);
      }
    });

    const hasGroupDuplicates = Array.from(groupNameCountMap.values()).some(count => count > 1);

    // Prüfe Panel-Duplikate (wenn gewünscht)
    let hasPanelDuplicates = false;

    if (includePanels) {
      // Hilfsfunktion zum rekursiven Prüfen von Panel-Duplikaten in einer Gruppe
      const checkPanelDuplicatesInGroup = (panels) => {
        if (!panels || panels.length === 0) return false;

        // Sammle alle normalen Panels (keine Untergruppen)
        const normalPanels = panels.filter(panel => panel.type !== 'group');
        const groupPanels = panels.filter(panel => panel.type === 'group');

        // Prüfe Duplikate unter den normalen Panels
        const nameCountMap = new Map();
        normalPanels.forEach(panel => {
          const trimmedTitle = panel.title?.trim();
          if (trimmedTitle) {
            nameCountMap.set(trimmedTitle, (nameCountMap.get(trimmedTitle) || 0) + 1);
          }
        });

        const hasLocalDuplicates = Array.from(nameCountMap.values()).some(count => count > 1);
        if (hasLocalDuplicates) return true;

        // Prüfe rekursiv in Untergruppen
        return groupPanels.some(panel => checkPanelDuplicatesInGroup(panel.panels));
      };

      // Prüfe in allen Hauptgruppen
      hasPanelDuplicates = groups.some(group => 
        group.panels && checkPanelDuplicatesInGroup(group.panels)
      );
    }

    return { hasGroupDuplicates, hasPanelDuplicates };
  }, [groups]);

  // Funktion zum Zusammenführen von doppelten Panelgruppen
  const mergeDuplicateGroups = useCallback((includePanels = false) => {
    if (!groups || groups.length === 0) return { groupCount: 0, panelCount: 0 };

    let totalPanelMerges = 0;

    // Schritt 1: ERST Gruppen zusammenführen (um Panel-Duplikate zu erzeugen)
    const nameCountMap = new Map();
    const duplicateNames = new Set();
    
    // Zähle Vorkommen jedes Gruppennamens
    groups.forEach(group => {
      const trimmedTitle = group.title?.trim();
      if (trimmedTitle) {
        const count = nameCountMap.get(trimmedTitle) || 0;
        nameCountMap.set(trimmedTitle, count + 1);
        if (count >= 1) {
          duplicateNames.add(trimmedTitle);
        }
      }
    });

    let groupMergeCount = 0;
    let processedGroups = groups;
    
    if (duplicateNames.size > 0) {
      // Erstelle neue Gruppen-Liste
      const newGroups = [];
      const processedNames = new Set();

      groups.forEach(group => {
        const trimmedTitle = group.title?.trim();
        
        if (!trimmedTitle || !duplicateNames.has(trimmedTitle)) {
          // Gruppe hat keinen Duplikaten, füge sie direkt hinzu
          newGroups.push(group);
        } else if (!processedNames.has(trimmedTitle)) {
          // Erste Gruppe mit diesem Namen - sammle alle Panels von Duplikaten
          processedNames.add(trimmedTitle);
          
          // Finde alle Gruppen mit gleichem Namen
          const groupsWithSameName = groups.filter(g => g.title?.trim() === trimmedTitle);
          
          // Sammle alle Panels aus allen Gruppen mit gleichem Namen
          const mergedPanels = [];
          groupsWithSameName.forEach((g) => {
            if (g.panels && Array.isArray(g.panels)) {
              mergedPanels.push(...g.panels);
            }
          });

          // Erstelle die zusammengeführte Gruppe (basierend auf der ersten Gruppe)
          const mergedGroup = {
            ...group,
            panels: mergedPanels
          };
          newGroups.push(mergedGroup);
          groupMergeCount += groupsWithSameName.length - 1;
        }
        // Weitere Gruppen mit gleichem Namen werden übersprungen
      });

      processedGroups = newGroups;
    }

    // Schritt 2: DANN Panels in den (möglicherweise zusammengeführten) Gruppen zusammenführen
    if (includePanels) {
      processedGroups = processedGroups.map((group) => {
        const result = mergeDuplicatePanelsInGroup(group.panels || []);
        totalPanelMerges += result.mergedCount;
        
        return {
          ...group,
          panels: result.panels
        };
      });
    }

    // Aktualisiere die Gruppen mit dem finalen Ergebnis
    setGroups(processedGroups);
    
    return { groupCount: groupMergeCount, panelCount: totalPanelMerges };
  }, [groups, setGroups, mergeDuplicatePanelsInGroup]);

  // Imperative Methoden für den Datenzugriff von außen
  useImperativeHandle(ref, () => ({
    getData: () => ({
      groups: groups
    }),
    setData: (data) => {
      if (data && data.groups) {
        // Prüfe ob sich die Daten tatsächlich geändert haben (ohne _reactKey)
        const cleanCurrentGroups = groups.map(g => {
          const cleanGroup = {...g};
          delete cleanGroup._reactKey;
          if (cleanGroup.panels) {
            cleanGroup.panels = cleanGroup.panels.map(p => {
              const cleanPanel = {...p};
              delete cleanPanel._reactKey;
              return cleanPanel;
            });
          }
          return cleanGroup;
        });
        
        const currentDataString = JSON.stringify(cleanCurrentGroups);
        const newDataString = JSON.stringify(data.groups);
        
        if (currentDataString === newDataString) {
          return;
        }
        
        // Hilfsfunktion zum Erhalten bestehender Zustände
        const preserveExistingStates = (newPanels, existingPanels = []) => {
          if (!newPanels) return newPanels;
          
          return newPanels.map(newPanel => {
            const existing = existingPanels.find(p => p.id === newPanel.id);
            
            if (newPanel.type === 'group' && newPanel.panels) {
              return {
                ...newPanel,
                open: existing?.open !== undefined ? existing.open : (newPanel.open !== undefined ? newPanel.open : true),
                panels: preserveExistingStates(newPanel.panels, existing?.panels)
              };
            } else {
              return {
                ...newPanel,
                open: existing?.open !== undefined ? existing.open : (newPanel.open !== undefined ? newPanel.open : true)
              };
            }
          });
        };

        // Hilfsfunktion für rekursive _reactKey Vergabe
        const addReactKeys = (panels, parentKey = '') => {
          return panels ? panels.map((panel, pIndex) => {
            const newPanel = {
              ...panel,
              _reactKey: `${panel.id}_${Date.now()}_${parentKey}_${pIndex}`
            };
            
            // Wenn es eine Untergruppe ist, verarbeite auch deren Panels
            if (newPanel.type === 'group' && newPanel.panels) {
              newPanel.panels = addReactKeys(newPanel.panels, `${panel.id}_${pIndex}`);
            }
            
            return newPanel;
          }) : [];
        };
        
        // Erhalte bestehende Zustände für Gruppen und Panels
        const groupsWithPreservedStates = data.groups.map((newGroup, index) => {
          const existingGroup = groups.find(g => g.id === newGroup.id);
          
          return {
            ...newGroup,
            open: existingGroup?.open !== undefined ? existingGroup.open : (newGroup.open !== undefined ? newGroup.open : true),
            panels: preserveExistingStates(newGroup.panels, existingGroup?.panels),
            _reactKey: `${newGroup.id}_${Date.now()}_${index}`
          };
        });

        // Force re-render durch komplett neues Array mit neuen IDs für React
        const newGroups = groupsWithPreservedStates.map((group, index) => ({
          ...group,
          panels: addReactKeys(group.panels, `group_${index}`)
        }));
        
        // Beim Laden von Daten direkt setGroupsInternal verwenden (ohne markAsChanged)
        setGroupsInternal(newGroups);
      }
    },
    forceSetData: (data) => {
      if (data && data.groups) {
        // Erstelle eine komplett neue Datenstruktur mit eindeutigen Keys
        const timestamp = Date.now();
        const forceKey = Math.random().toString(36).substr(2, 9);
        
        // Hilfsfunktion für rekursive _reactKey Vergabe mit garantiert eindeutigen Keys
        const addReactKeys = (panels, parentKey = '') => {
          return panels ? panels.map((panel, pIndex) => {
            const newPanel = {
              ...panel,
              _reactKey: `${panel.id}_${timestamp}_${forceKey}_${parentKey}_${pIndex}`
            };
            
            // Wenn es eine Untergruppe ist, verarbeite auch deren Panels
            if (newPanel.type === 'group' && newPanel.panels) {
              newPanel.panels = addReactKeys(newPanel.panels, `${panel.id}_${pIndex}`);
            }
            
            return newPanel;
          }) : [];
        };
        
        // Force re-render durch komplett neues Array mit eindeutigen IDs für React
        const newGroups = data.groups.map((group, index) => ({
          ...group,
          _reactKey: `${group.id}_${timestamp}_${forceKey}_${index}`,
          panels: addReactKeys(group.panels, `group_${index}`)
        }));
        
        // Beim Undo/Redo ebenfalls direkt setGroupsInternal verwenden (ohne markAsChanged)
        setGroupsInternal(newGroups);
      }
    },
    triggerSnapshot: () => {
      if (onDataChange) {
        onDataChange();
      }
    },
    mergeDuplicateGroups: mergeDuplicateGroups,
    checkForDuplicates: checkForDuplicates
  }), [groups, onDataChange, mergeDuplicateGroups, checkForDuplicates]); // Abhängigkeiten hinzufügen

  // Debug: Verfolge Änderungen an groups
  useEffect(() => {
  }, [groups]);

  // Effekt um Änderungen zu melden
  useEffect(() => {
    if (onDataChange) {
      // Sofortige Ausführung ohne Verzögerung
      onDataChange();
    }
  }, [groups, onDataChange]);

  // dnd-kit setup
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Vereinfachte rekursive Hilfsfunktionen
  const findPanelInData = (panelId, data) => {
    for (let i = 0; i < data.length; i++) {
      const group = data[i];
      for (let j = 0; j < group.panels.length; j++) {
        const panel = group.panels[j];
        if (panel.id === panelId) {
          return { groupId: group.id, panelIndex: j, panel };
        }
        if (panel.type === 'group' && panel.panels) {
          const found = findPanelInData(panelId, [panel]);
          if (found) return found;
        }
      }
    }
    return null;
  };

  const findGroupInData = (groupId, data) => {
    // Prüfe zuerst Hauptgruppen (nur die oberste Ebene)
    for (const group of data) {
      if (group.id === groupId) return { group, isMainGroup: true };
    }
    // Dann prüfe Untergruppen (in den panels)
    for (const group of data) {
      for (const panel of group.panels) {
        if (panel.type === 'group' && panel.id === groupId) {
          return { group: panel, parentGroupId: group.id, isMainGroup: false };
        }
        if (panel.type === 'group' && panel.panels) {
          const found = findGroupInData(groupId, [{ id: 'temp', panels: panel.panels }]);
          if (found) return { ...found, isMainGroup: false };
        }
      }
    }
    return null;
  };

  const removePanelFromData = (panelId, data) => {
    return data.map(group => ({
      ...group,
      panels: group.panels.filter(panel => panel.id !== panelId).map(panel => {
        if (panel.type === 'group' && panel.panels) {
          return {
            ...panel,
            panels: removePanelFromData(panelId, [panel])[0].panels
          };
        }
        return panel;
      })
    }));
  };

  const addPanelToGroupInData = (targetGroupId, panel, data) => {
    return data.map(group => {
      if (group.id === targetGroupId) {
        return {
          ...group,
          panels: [...group.panels, panel]
        };
      }
      return {
        ...group,
        panels: group.panels.map(p => {
          if (p.type === 'group') {
            if (p.id === targetGroupId) {
              return {
                ...p,
                panels: [...(p.panels || []), panel]
              };
            } else if (p.panels) {
              const updated = addPanelToGroupInData(targetGroupId, panel, [p]);
              return {
                ...p,
                panels: updated[0].panels
              };
            }
          }
          return p;
        })
      };
    });
  };

  const removeGroupFromData = (groupId, data) => {
    const result = data.filter(group => group.id !== groupId).map(group => ({
      ...group,
      panels: removeGroupFromPanels(groupId, group.panels)
    }));
    return result;
  };

  const removeGroupFromPanels = (groupId, panels) => {
    return panels.filter(panel => panel.id !== groupId).map(panel => {
      if (panel.type === 'group' && panel.panels) {
        return {
          ...panel,
          panels: removeGroupFromPanels(groupId, panel.panels)
        };
      }
      return panel;
    });
  };

  const handleDragStart = (event) => {
    const { active } = event;
    // Versuche zuerst Panel zu finden
    const foundPanel = findPanelInData(active.id, groups);
    if (foundPanel) {
      setActivePanel(foundPanel.panel);
      return;
    }
    // Dann versuche Gruppe zu finden
    const foundGroup = findGroupInData(active.id, groups);
    if (foundGroup) {
      setActivePanel(foundGroup.group);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      setActivePanel(null);
      return;
    }

    // Prüfe zuerst, ob es sich um eine Gruppe handelt (als Quelle)
    const sourceGroup = findGroupInData(active.id, groups);
    
    // Dann prüfe, ob es sich um ein normales Panel handelt
    const sourcePanel = findPanelInData(active.id, groups);
    
    // Behandle Gruppen-Bewegungen
    if (sourceGroup) {
      // Behandle Promotion zur Hauptgruppe
      if (sourceGroup.isMainGroup === false && (over.id === 'promote-to-main' || over.id === 'main-level')) {
        const subgroupToMove = sourceGroup.group;
        
        if (subgroupToMove && !isPromoting) {
          setIsPromoting(true);
          
          setTimeout(() => {
            setGroups(prevGroups => {
              
              // Entferne die Untergruppe aus den Panels
              const updatedGroups = removePanelFromData(active.id, prevGroups);
              
              // Erstelle neue Hauptgruppe aus der Untergruppe
              const newMainGroup = {
                id: subgroupToMove.id,
                title: subgroupToMove.title,
                textColor: subgroupToMove.textColor || '#000000',
                backgroundColor: subgroupToMove.backgroundColor || 'rgba(248, 249, 250, 0.9)',
                panels: subgroupToMove.panels || []
              };
              
              const finalResult = [...updatedGroups, newMainGroup];
              
              return finalResult;
            });
            
            setIsPromoting(false);
          }, 0);
        }
        setActivePanel(null);
        return;
      }

      const targetGroup = findGroupInData(over.id, groups);
      
      // Prüfe alle möglichen Szenarien in der richtigen Reihenfolge
      if (sourceGroup.isMainGroup && targetGroup && targetGroup.isMainGroup === false) {
        // Hauptgruppe zu Untergruppe: Hauptgruppe in eine andere Gruppe einfügen
        const mainGroupToMove = groups.find(g => g.id === active.id);
        if (mainGroupToMove) {
          setGroups(prevGroups => {
            // Entferne Hauptgruppe
            const updatedGroups = prevGroups.filter(g => g.id !== active.id);
            // Erstelle Untergruppe aus Hauptgruppe
            const groupAsSubgroup = {
              ...mainGroupToMove,
              type: 'group'
            };
            // Füge als Untergruppe hinzu
            return addPanelToGroupInData(over.id, groupAsSubgroup, updatedGroups);
          });
        }
        setActivePanel(null);
        return;
      } else if (sourceGroup.isMainGroup && targetGroup && targetGroup.isMainGroup) {
        // Hauptgruppe auf andere Hauptgruppe: Als Untergruppe verschachteln
        const mainGroupToMove = groups.find(g => g.id === active.id);
        if (mainGroupToMove && active.id !== over.id) {
          setGroups(prevGroups => {
            // Entferne Hauptgruppe
            const updatedGroups = prevGroups.filter(g => g.id !== active.id);
            // Erstelle Untergruppe aus Hauptgruppe
            const groupAsSubgroup = {
              ...mainGroupToMove,
              type: 'group'
            };
            // Füge als Untergruppe hinzu
            return addPanelToGroupInData(over.id, groupAsSubgroup, updatedGroups);
          });
        }
        setActivePanel(null);
        return;
      } else if (sourceGroup.isMainGroup === false && targetGroup && targetGroup.isMainGroup) {
        // Untergruppe zu Hauptgruppe: Untergruppe zur Hauptebene befördern
        const subgroupToMove = sourceGroup.group;
        if (subgroupToMove) {
          // Entferne Untergruppe aus ihrer aktuellen Position
          const updatedGroups = removeGroupFromData(active.id, groups);
          // Erstelle neue Hauptgruppe
          const newMainGroup = {
            id: subgroupToMove.id,
            title: subgroupToMove.title,
            textColor: subgroupToMove.textColor || '#000000',
            backgroundColor: subgroupToMove.backgroundColor || 'rgba(248, 249, 250, 0.9)',
            panels: subgroupToMove.panels || []
          };
          // Füge an der Position der Zielgruppe ein
          const targetIndex = updatedGroups.findIndex(g => g.id === over.id);
          const finalGroups = [...updatedGroups];
          finalGroups.splice(targetIndex + 1, 0, newMainGroup);
          setGroups(finalGroups);
        }
        setActivePanel(null);
        return;
      } else if (sourceGroup.isMainGroup === false && targetGroup && targetGroup.isMainGroup === false && active.id !== over.id) {
        // Untergruppe zu Untergruppe: In eine andere Untergruppe verschieben
        const subgroupToMove = sourceGroup.group;
        if (subgroupToMove) {
          const updatedGroups = removeGroupFromData(active.id, groups);
          const finalGroups = addPanelToGroupInData(over.id, subgroupToMove, updatedGroups);
          setGroups(finalGroups);
        }
        setActivePanel(null);
        return;
      }
    }

    // Behandle Panel-Bewegungen (nur wenn keine Gruppe als Quelle gefunden wurde)
    if (!sourceGroup && sourcePanel) {
      
      // Prüfe, ob es sich um eine Untergruppe handelt (type: 'group')
      if (sourcePanel.panel.type === 'group' && (over.id === 'promote-to-main' || over.id === 'main-level')) {
        const subgroupToMove = sourcePanel.panel;
        
        if (subgroupToMove && !isPromoting) {
          setIsPromoting(true);
          
          // Verwende setTimeout um sicherzustellen, dass andere Updates abgeschlossen sind
          setTimeout(() => {
            setGroups(prevGroups => {
              
              // Entferne die Untergruppe aus den Panels (verwende die Panel-Remove-Funktion)
              const updatedGroups = removePanelFromData(active.id, prevGroups);
              
              // Erstelle neue Hauptgruppe aus der Untergruppe
              const newMainGroup = {
                id: subgroupToMove.id,
                title: subgroupToMove.title,
                textColor: subgroupToMove.textColor || '#000000',
                backgroundColor: subgroupToMove.backgroundColor || 'rgba(248, 249, 250, 0.9)',
                panels: subgroupToMove.panels || []
              };
              
              // Füge die neue Hauptgruppe hinzu
              const finalResult = [...updatedGroups, newMainGroup];
              
              return finalResult;
            });
            
            setIsPromoting(false);
          }, 0);
        }
        setActivePanel(null);
        return;
      }
      
      // Panel-Logic für normale Panels und Gruppen-Verschiebungen
      let targetGroupId = null;
      
      // Prüfe, ob das Ziel eine Gruppe ist (Haupt- oder Untergruppe)
      const targetGroupInfo = findGroupInData(over.id, groups);
      if (targetGroupInfo) {
        targetGroupId = over.id;
      } else {
        // Prüfe, ob das Ziel ein Panel ist, und finde die zugehörige Gruppe
        const targetPanel = findPanelInData(over.id, groups);
        if (targetPanel) {
          targetGroupId = targetPanel.groupId;
        }
      }

      
      if (targetGroupId && targetGroupId !== sourcePanel.groupId) {
        const updatedGroups = removePanelFromData(active.id, groups);
        const finalGroups = addPanelToGroupInData(targetGroupId, sourcePanel.panel, updatedGroups);
        setGroups(finalGroups);
      }
      setActivePanel(null);
      return;
    }

    setActivePanel(null);
    
    // Prüfe nach Drag & Drop auf Duplikate
    if (onCheckForDuplicates) {
      setTimeout(onCheckForDuplicates, 100);
    }
  };

  const handleAddGroup = () => {
    if (newGroupTitle.trim() === '') return;
    
    setGroups([
      ...groups,
      {
        id: `group-${Date.now()}`,
        title: newGroupTitle,
        textColor: '#000000',
        backgroundColor: 'rgba(253, 255, 229, 0.9)',
        borderStyle: 'solid',
        borderColor: '#bbb',
        borderThickness: '1',
        panels: []
      }
    ]);
    setNewGroupTitle('');
    setShowDialog(false);
    
    // Prüfe nach dem Hinzufügen auf Duplikate
    if (onCheckForDuplicates) {
      setTimeout(onCheckForDuplicates, 100);
    }
  };

  const handleTitleChange = (groupId, newTitle) => {
    setGroups(groups =>
      groups.map(g =>
        g.id === groupId ? { ...g, title: newTitle } : g
      )
    );
    
    // Prüfe nach dem Umbenennen auf Duplikate
    if (onCheckForDuplicates) {
      setTimeout(onCheckForDuplicates, 100);
    }
  };

  const handlePanelsChange = (groupId, newPanels) => {
    setGroups(groups =>
      groups.map(g =>
        g.id === groupId ? { ...g, panels: newPanels } : g
      )
    );
    
    // Prüfe nach Änderungen an Panels auf Duplikate
    if (onCheckForDuplicates) {
      setTimeout(onCheckForDuplicates, 100);
    }
  };

  const handleMoveGroupUp = (groupIndex) => {
    if (groupIndex > 0) {
      const newGroups = [...groups];
      [newGroups[groupIndex], newGroups[groupIndex - 1]] = [newGroups[groupIndex - 1], newGroups[groupIndex]];
      setGroups(newGroups);
    }
  };

  const handleMoveGroupDown = (groupIndex) => {
    if (groupIndex < groups.length - 1) {
      const newGroups = [...groups];
      [newGroups[groupIndex], newGroups[groupIndex + 1]] = [newGroups[groupIndex + 1], newGroups[groupIndex]];
      setGroups(newGroups);
    }
  };

  const { isEditing, showEditButtons } = useEditing();

  return (
    <div>
      {/* Visueller Hinweis wenn Drag and Drop deaktiviert ist */}
      {isEditing && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          backgroundColor: '#ff9800',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '4px',
          fontSize: '14px',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          fontWeight: '500'
        }}>
          🚫 Drag & Drop während Bearbeitung deaktiviert
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={isAuthor && !isEditing ? handleDragStart : undefined}
        onDragEnd={isAuthor && !isEditing ? handleDragEnd : undefined}
      >
        <div>
          {groups.map((group, idx) => {
            const canMoveUp = idx > 0;
            const canMoveDown = idx < groups.length - 1;
            
            return (
              <DraggableMainGroup key={group._reactKey || group.id} group={group} isAuthor={isAuthor}>
                <div style={{ marginBottom: 16, position: 'relative' }}>
                  <PanelGroup
                    groupTitle={group.title}
                    textColor={group.textColor || '#000000'}
                    backgroundColor={group.backgroundColor || '#f8f9fa'}
                    borderStyle={group.borderStyle || 'solid'}
                    borderColor={group.borderColor || '#bbb'}
                    borderThickness={group.borderThickness || '1'}
                    open={group.open !== undefined ? group.open : true}
                    onOpenChange={(newOpen) => {
                      setGroups(groups.map(g =>
                        g.id === group.id 
                          ? { ...g, open: newOpen }
                          : g
                      ));
                    }}
                    onSave={(newTitle, newTextColor, newBackgroundColor, newBorderStyle, newBorderColor, newBorderThickness) => {
                      setGroups(groups.map(g =>
                        g.id === group.id 
                          ? { ...g, title: newTitle, textColor: newTextColor, backgroundColor: newBackgroundColor, borderStyle: newBorderStyle, borderColor: newBorderColor, borderThickness: newBorderThickness }
                          : g
                      ));
                    }}
                    onTitleChange={title => handleTitleChange(group.id, title)}
                    panelsInit={group.panels}
                    isAuthor={isAuthor}
                    onPanelsChange={panels => handlePanelsChange(group.id, panels)}
                    groupId={group.id}
                    canMoveUp={canMoveUp}
                    canMoveDown={canMoveDown}
                    onMoveUp={() => handleMoveGroupUp(idx)}
                    onMoveDown={() => handleMoveGroupDown(idx)}
                    visibleWeeks={visibleWeeks}
                    weekWidth={weekWidth}
                    isVisibleWeeksChanging={isVisibleWeeksChanging}
                    isZoomChanging={isZoomChanging}
                    onInfoPanelsChange={onInfoPanelsChange}
                    batchOperations={batchOperations}
                    onCheckForDuplicates={onCheckForDuplicates}
                    onDelete={() => {
                      // Hauptgruppe löschen
                      const newGroups = groups.filter(g => g.id !== group.id);
                      setGroups(newGroups);
                      
                      // Prüfe nach dem Löschen auf Duplikate
                      if (onCheckForDuplicates) {
                        setTimeout(onCheckForDuplicates, 100);
                      }
                    }}
                  />
                </div>
              </DraggableMainGroup>
            );
          })}
          {/* Separate Drop-Zone für Untergruppen zu Hauptgruppen */}
          {isAuthor && showEditButtons && (
            <DroppablePromoteZone onAddGroup={() => setShowDialog(true)} />
          )}
        </div>
        <DragOverlay>
          {activePanel ? (
            <div style={{
              border: '1px solid #ddd',
              borderRadius: 6,
              background: '#fafafa',
              padding: '12px 16px',
              fontWeight: 'bold',
              fontSize: 18,
              opacity: 0.8,
              transform: 'rotate(5deg)'
            }}>
              {activePanel.title}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      {showDialog && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: '#fff',
            padding: 24,
            borderRadius: 8,
            minWidth: 320,
            boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }}>
            <span style={{ fontWeight: 'bold', fontSize: 18 }}>Neue Panelgruppe anlegen</span>
            <input
              type="text"
              placeholder="Panelgruppen-Titel"
              value={newGroupTitle}
              onChange={e => setNewGroupTitle(e.target.value)}
              style={{ padding: 8, fontSize: 16, borderRadius: 4, border: '1px solid #ccc' }}
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') handleAddGroup();
                if (e.key === 'Escape') setShowDialog(false);
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setShowDialog(false)}
                style={{
                  background: '#eee',
                  color: '#333',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 16px',
                  cursor: 'pointer'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleAddGroup}
                style={{
                  background: '#222',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 16px',
                  cursor: 'pointer'
                }}
                disabled={newGroupTitle.trim() === ''}
              >
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});